import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { Battery, Activity, Thermometer, Heart } from 'lucide-react';
import { DashboardCard } from '@/components/ui/dashboard-card.tsx';
import { useOverviewBESSQuery } from '@/features/bess/hooks/queries/[bess]/get-bess-overview.ts';
import { cn } from '@/utils/utils.ts';
import style from './bess-metrics.module.scss';

const POWER_STATUS_COLORS = {
  Idle: 'bg-yellow-500',
  Charging: 'bg-green-500',
  Discharging: 'bg-red-500',
  default: 'bg-gray-200',
};

interface BESSOverview {
  batterySoC?: number;
  batteryPowerStatus?: string;
  batteryTemperature?: number;
  batterySoH?: number;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getMetricConfigs = (overview: BESSOverview | undefined, t: (...args: any[]) => any) => [
  {
    value: overview?.batterySoC?.toFixed(2),
    unit: '%',
    label: t('battery_state_of_charge'),
    showProgressBar: true,
    minValue: 0,
    maxValue: 100,
    icon: <Battery className="text-gray-500" size={32} />,
  },
  {
    value: overview?.batteryPowerStatus,
    label: t('battery_charge_discharge_status'),
    showProgressBar: true,
    isPowerStatus: true,
    icon: <Activity className="text-gray-500" size={32} />,
  },
  {
    value: overview?.batteryTemperature?.toFixed(2),
    unit: '°C',
    label: t('battery_temperature'),
    showProgressBar: true,
    minValue: overview?.batteryTemperature ? -20 : 0,
    maxValue: 60,
    icon: <Thermometer className="text-gray-500" size={32} />,
  },
  {
    value: overview?.batterySoH?.toFixed(2),
    unit: '%',
    label: t('battery_state_of_health'),
    showProgressBar: true,
    progressBarColor: 'bg-green-500',
    minValue: 0,
    maxValue: 100,
    icon: <Heart className="text-gray-500" size={32} />,
  },
];

export const TodayMetricsBESS = ({ className }: { className?: string }) => {
  const { bessId } = useParams();
  const { data: overview, isLoading } = useOverviewBESSQuery({ id: bessId });
  const { t } = useTranslation(['translation'], { keyPrefix: 'metrics' });

  const metricConfigs = getMetricConfigs(overview, t);

  return (
    <div className={cn(style.today_metrics, className)}>
      <div className={style.today_metrics__cards}>
        {metricConfigs.map((config, index) => (
          <MetricCard key={index} isLoading={isLoading} {...config} />
        ))}
      </div>
    </div>
  );
};

interface MetricCardProps {
  label: string;
  value: string | undefined;
  unit?: string;
  icon?: ReactNode;
  isLoading: boolean;
  showProgressBar?: boolean;
  progressBarColor?: string;
  isPowerStatus?: boolean;
  minValue?: number;
  maxValue?: number;
}

const calculateProgressPercentage = (
  value: string | undefined,
  isPowerStatus: boolean,
  minValue: number,
  maxValue: number
): number => {
  if (isPowerStatus) return 100;
  if (!value) return 0;

  const numValue = parseFloat(value);
  const percentage = ((numValue - minValue) / (maxValue - minValue)) * 100;
  return Math.min(Math.max(percentage, 0), 100);
};

const getProgressBarColor = (
  isPowerStatus: boolean,
  value: string | undefined,
  customColor?: string
): string => {
  if (isPowerStatus) {
    return (
      POWER_STATUS_COLORS[value as keyof typeof POWER_STATUS_COLORS] ||
      POWER_STATUS_COLORS.default
    );
  }
  return customColor || 'bg-blue-500';
};

const MetricCard = ({
  label,
  value,
  unit,
  icon,
  isLoading,
  showProgressBar = false,
  progressBarColor,
  isPowerStatus = false,
  minValue = 0,
  maxValue = 100,
}: MetricCardProps) => {
  const progressPercentage = calculateProgressPercentage(
    value,
    isPowerStatus,
    minValue,
    maxValue
  );
  const barColor = getProgressBarColor(isPowerStatus, value, progressBarColor);

  return (
    <DashboardCard className="h-[100px] min-w-[220px]" isLoading={isLoading}>
      <div className="flex h-full flex-col justify-between">
        <div className="flex justify-between">
          <div className="flex flex-col gap-3 px-2">
            <span className="text-muted-foreground text-sm text-nowrap">{label}</span>
            <div className="flex items-end gap-2">
              <span className="text-2xl font-semibold">
                {value || '--'}{' '}
                {value && unit && (
                  <span className="text-muted-foreground text-xl">{unit}</span>
                )}
              </span>
            </div>
          </div>
          {icon && (
            <div className="self-center">
              <div className="flex size-14 items-center justify-center">{icon}</div>
            </div>
          )}
        </div>

        {showProgressBar && (
          <div className="relative mt-2 h-1.5 w-full rounded-full bg-gray-200">
            <div
              className={`h-full rounded-full ${barColor}`}
              style={{ width: `${progressPercentage}%` }}
            ></div>
          </div>
        )}
      </div>
    </DashboardCard>
  );
};
